import React, { useEffect } from "react";
import { connect } from "react-redux";
import { Form, Input, DatePicker, TimePicker, Select } from "antd";
import CrudButtons from "components/button/crudbuttons";
import FormItem from "components/fields/FormItem";
import dayjs from "dayjs";
import { requiredField } from "utils/crudSagaHelper";
import {
  getEmployeeScheduleDetails,
  doEmployeeScheduleCreate,
  doEmployeeScheduleUpdate,
} from "../../../../../redux/employee/employeeState";
import { DATE_FORMAT, TIME_FORMAT } from "variables/constants";
import SwitchWithSpecify from "components/fields/SwitchWithSpecify";
import { formatDateTimeOrNull } from "utils";
import { MODULE_EMPLOYEES } from "variables/constants";

const EmployeeScheduleForm = ({ 
  recordData, 
  doCreate, 
  doUpdate, 
  onCancel, 
  getDetails,
  employeeId,
  submitLabel,
  ...props
}) => {
  const [form] = Form.useForm();
  const { recordId, mode } = props;

  const daysOfWeek = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",];

  useEffect(() => {
    if (recordData ) {
      form.resetFields();
      const { employee_schedule:employeeSchedule, schedules, ...rest } = recordData;
      
      const initialValues = {
        ...rest,
        ...employeeSchedule,
        esd_employee_id: employeeId,
        esd_effective_date_from: recordData?.esd_effective_date_from ? dayjs(recordData.esd_effective_date_from) : null,
        esd_effective_date_to: recordData?.esd_effective_date_to ? dayjs(recordData.esd_effective_date_to) : null,
        schedules: (schedules || []).map(schedule => ({
          ...schedule,
          esd_time_in: schedule.esd_time_in ? dayjs(schedule.esd_time_in, TIME_FORMAT) : null,
          esd_time_out: schedule.esd_time_out ? dayjs(schedule.esd_time_out, TIME_FORMAT ) : null,
          esd_flexible_time: schedule.esd_flexible_time ?? null,
        })),
      };

      form.setFieldsValue(initialValues);
    }
  }, [recordData, employeeId, form]);

  useEffect(() => {
    if (employeeId) {
      getDetails({ employeeId, scheduleId: recordId });
    }
  }, [employeeId, recordId, getDetails]);

  const onSubmit = (formData) => { 
    console.log(formData);
    try {
      formData.esd_effective_date_from = formatDateTimeOrNull(formData.esd_effective_date_from, DATE_FORMAT);
      formData.esd_effective_date_to = formatDateTimeOrNull(formData.esd_effective_date_to, DATE_FORMAT);
      formData.esd_employee_id = employeeId;
      
      formData.schedules = formData.schedules.map(schedule => ({
        ...schedule,
        esd_employee_id: employeeId,
        esd_effective_date_from: formatDateTimeOrNull(formData.esd_effective_date_from, DATE_FORMAT),
        esd_effective_date_to: formatDateTimeOrNull(formData.esd_effective_date_to, DATE_FORMAT),
        esd_time_in: formatDateTimeOrNull(schedule.esd_time_in, TIME_FORMAT),
        esd_time_out: formatDateTimeOrNull(schedule.esd_time_out, TIME_FORMAT),
        esd_is_flexible: Number(Boolean(schedule.esd_is_flexible)),
      }));
  
      console.log(formData);
      recordData?.esd_id > 0 ? doUpdate(formData) : doCreate(formData);
      
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  return (
    <Form className={mode !== "view" ? "form" : ""}
      layout="vertical" 
      form={form} 
      onFinish={onSubmit}
    > 
      <FormItem
        name="esd_employee_id"
        hidden
      >
        <Input type="hidden"/>
      </FormItem>
      <FormItem
        name="esd_id"
        hidden
      >
        <Input type="hidden"/>
      </FormItem>
      <div className="mb-0">
        <div className="grid grid-cols-4 gap-y-2 gap-x-4 mb-2 w-full">
          <FormItem extra="col-span-1 col-start-2"
              label="Effective Date From"
              name="esd_effective_date_from"
              rules={[requiredField()]}
          >
            {mode !== "view" ?
              <DatePicker id="esd_effective_date_from"
                className="input-field"
                format={DATE_FORMAT}
                style={{
                  width: "100%",
                }}
              />
            : 
              <label className="text-base label-value dark:text-white">
                {recordData?.esd_effective_date_from}
              </label>
            }
          </FormItem>
          <FormItem 
            label={<span className="px-2">Effective Date To</span>}
            name="esd_effective_date_to"
          >
            {mode !== "view" ?
              <DatePicker id="esd_effective_date_to"
                className="input-field"
                format={DATE_FORMAT}
                style={{
                  width: "100%",
                }}
              />
            : 
              <label className="text-base label-value dark:text-white">
                {recordData?.esd_effective_date_to}
              </label>
            }
          </FormItem>
        </div>
        <div className="w-full ">
          <Form.List name="schedules" initialValue={recordData?.schedules || []}>
            {(fields) =>
              fields.map((field, index) => (
                <div className="grid grid-cols-5 gap-y-2 gap-x-4 mb-2 w-full" key={`${field.key}`}>
                  <FormItem
                    {...field}
                    name={[index, "esd_day"]}
                    initialValue={index}
                    hidden
                  >
                    <Input type="hidden" />
                  </FormItem>
                  <FormItem 
                    {...field}
                    name={[index, "esd_id"]}
                    hidden
                  >
                    <Input type="hidden"/>
                  </FormItem>
                  <FormItem 
                    {...field} 
                    name={[index, "esd_type_id"]}
                    label={daysOfWeek[index] || `Day ${index + 1}`}
                    rules={[requiredField()]}
                  >
                    <Select id="esd_type_id"
                      className="input-field"
                      showSearch
                      allowClear={true}
                      options={recordData?.scheduleTypeOptions}
                    /> 
                  </FormItem>	
                  <FormItem
                    {...field} 
                    name={[index, "esd_time_in"]}
                    label={<span className="px-2">Time In</span>}
                  >
                    <TimePicker 
                      id={`esd_time_in_${index}`}
                      className="input-field "
                      maxLength={10}
                      format={TIME_FORMAT}
                      placeholder="00:00"
                    />
                  </FormItem>	
                  <FormItem
                    {...field} 
                    name={[index, "esd_time_out"]}
                    label={<span className="px-2">Time Out</span>}
                  >
                    <TimePicker
                      id={`esd_time_out_${index}`}
                      className="input-field "
                      maxLength={10}
                      format={TIME_FORMAT}
                      placeholder="00:00"
                    />
                  </FormItem>
                  <FormItem extra="col-span-2 "
                    {...field}
                    valuePropName="checked"
                    name={[index, "esd_is_flexible"]}
                    label={<span className="px-2">Is Flexible</span>}
                  >
                    <SwitchWithSpecify
                      mode="edit"
                      id={[index, "esd_is_flexible"]}
                      type="text" 
                      labelYes="Yes"
                      labelNo="No"
                      switchValue={recordData?.schedules?.[index]?.esd_is_flexible ?? false}
                      specicifyDisplayValue={[index, "esd_flexible_time"]}
                      specifyFieldName={[index, "esd_flexible_time"]}
                      specifyPlaceholder="Enter flexible time"
                    />
                  </FormItem>
                </div>
              ))
            }
          </Form.List>
        </div>
      </div>

      <div className="w-full flex justify-end">
        {(mode === "edit") && (
          <CrudButtons
            mode={mode}
            recordId={recordData?.esd_id ?? 0}
            isModal={true}
            modalId={recordData?.esd_id > 0 ? 'edit_employee_schedule' : 'add_employee_schedule'}
            cancelLabel="Cancel"
            doHideModal={onCancel}
            submitLabel={submitLabel}
            moduleId={MODULE_EMPLOYEES}
          />
        )}
      </div>
    </Form>
  );
};

const mapStateToProps = (state) => ({
  recordData: state.employee.employeeScheduleDetails?.data,
});

const mapDispatchToProps = (dispatch) => ({
  getDetails: (data) => dispatch(getEmployeeScheduleDetails(data)),
  doCreate: (data) => dispatch(doEmployeeScheduleCreate(data)),
  doUpdate: (data) => dispatch(doEmployeeScheduleUpdate(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeScheduleForm);
